/**
 * Zoom du tableau : deux boutons et un curseur, poses SUR le bas de l'image.
 *
 * Il est la et pas dans la barre d'outils, parce qu'il ne peint rien : melange
 * aux pinceaux, un curseur de zoom se confond avec un reglage de matiere, et
 * c'est exactement la confusion qu'on cherche a lever (le curseur de taille a
 * deja ete pris pour lui).
 *
 * L'echelle est LOGARITHMIQUE : de x0,8 a x12, une graduation lineaire tasserait
 * tout l'interval utile (x1 a x3) dans le premier cinquieme de la course.
 */
import type { ReactElement } from 'react';
import { useT } from '../locales/useT.js';
import { MAX_ZOOM_FACTOR, MIN_ZOOM_FACTOR } from './canvas/renderConfig.js';

/** Un cran de bouton : un tiers de plus, un quart de moins. */
const STEP = 1.35;
const TRACK = 1000;

function toSlider(factor: number): number {
  const clamped = Math.min(Math.max(factor, MIN_ZOOM_FACTOR), MAX_ZOOM_FACTOR);
  const ratio = Math.log(clamped / MIN_ZOOM_FACTOR) / Math.log(MAX_ZOOM_FACTOR / MIN_ZOOM_FACTOR);
  return Math.round(ratio * TRACK);
}

function fromSlider(position: number): number {
  const ratio = Math.min(Math.max(position, 0), TRACK) / TRACK;
  return MIN_ZOOM_FACTOR * (MAX_ZOOM_FACTOR / MIN_ZOOM_FACTOR) ** ratio;
}

export function ZoomControl({
  zoom,
  onZoom,
}: {
  readonly zoom: number;
  readonly onZoom: (factor: number) => void;
}): ReactElement {
  const t = useT();
  const step = (factor: number): void =>
    onZoom(Math.min(Math.max(zoom * factor, MIN_ZOOM_FACTOR), MAX_ZOOM_FACTOR));

  return (
    // L'ancre est un element de hauteur NULLE, glisse entre le canvas et la
    // barre d'outils : elle donne au controle le bas de l'image comme repere,
    // sans dependre de la hauteur de la barre, qui change avec la palette.
    <div className="zoom-anchor">
      <div className="zoom-control">
        <button
          type="button"
          className="zoom-control__button"
          aria-label={t('tool.zoomOut')}
          title={t('tool.zoomOut')}
          disabled={zoom <= MIN_ZOOM_FACTOR + 0.001}
          onClick={() => step(1 / STEP)}
        >
          −
        </button>
        <input
          type="range"
          className="zoom-control__range"
          min={0}
          max={TRACK}
          value={toSlider(zoom)}
          aria-label={t('tool.zoom')}
          onChange={(event) => onZoom(fromSlider(Number(event.target.value)))}
        />
        <button
          type="button"
          className="zoom-control__button"
          aria-label={t('tool.zoomIn')}
          title={t('tool.zoomIn')}
          disabled={zoom >= MAX_ZOOM_FACTOR - 0.001}
          onClick={() => step(STEP)}
        >
          +
        </button>
      </div>
    </div>
  );
}
